import type { GetRecommendationsParams, StoreSearchParams } from '@libs/common-types';
import { getLocale, Locale, Stores } from '@libs/core';

export const CWEA_ENDPOINTS = {
    account: {
        anonymousId: `account/anonymousId`,
        accounts: 'account/accounts',
        enrollmentProfile: (key: string) => `account/enrollmentProfile?generateSecuredKey=${key}`,
    },
    cart: {
        fetchVetAuthImage: (cartId?: string, imageId?: string) => `carts/${cartId}/items/vetAuth/${imageId}`,
        uploadVetAuthImage: (cartId?: string, itemId?: string) => `carts/${cartId}/items/${itemId}/vetAuth`,
    },
    customerCare: {
        contactUs: `customerCare/contactUs`,
    },
    loyalty: {
        activateOffer: `loyalty/activateOffer`,
        getLoyaltyPoints: `loyalty/points`,
        getOffers: `loyalty/getLoyaltyOffers`,
        getMemberPoints: `loyalty/getMemberPoints`,
        getPointsHistory: `loyalty/pointsHistory`,
    },
    inventory: {
        getProductInventory: (sku: string) => `inventory/${sku}`,
        getProductInventoryPairs: `inventory`,
    },
    recommendations: {
        getProductRecommendations: ({ productId, customerId, storeId, locale }: GetRecommendationsParams) =>
            `recommendations/customer/${customerId}/product/${productId}/store/${storeId}/locale/${locale}`,
    },
    stores: {
        getStoreById: (storeId: string) => `stores/v2/${storeId}`,
        getDisplayStoreById: (storeId: string) => `stores/v2/${storeId}`,
        getWebStoreById: () => {
            const locale = getLocale();
            const storeId = locale === Locale['en-US'] ? Stores.US : Stores.CA;

            return `stores/v2/web/${storeId}`;
        },
        search: (params: StoreSearchParams) => {
            const locale = getLocale();
            const searchParams = new URLSearchParams({
                locale,
            });

            Object.entries(params).forEach(([key, value]) => {
                if (typeof value !== 'undefined' && value !== null) {
                    searchParams.append(key, String(value));
                }
            });

            const queryString = searchParams.toString();
            return `stores/v2/search?${queryString}`;
        },
    },
    discounts: {
        getDiscountDetails: (key: string) => `discounts/${key}/details`,
    },
    orders: {
        getOrders: `orders`,
        getOrder: ({ orderId, email, locale }: { orderId: string; email?: string | null; locale: string }) => {
            return email
                ? `orders/${orderId}?email=${encodeURIComponent(email.trim().replaceAll(' ', '+'))}&locale=${locale}`
                : `orders/${orderId}?locale=${locale}`;
        },
    },
    pets: {
        getPets: `pets`,
        addPet: `pets`,
        removePet: (petId: string) => `pets/${petId}`,
        editPet: (petId: string) => `pets/${petId}`,
        getPetsConfig: `pets/config`,
        uploadPetPhoto: (petId: string) => `pets/${petId}/photos`,
    },
    appointments: {
        getAppointments: `appointments`,
        updateAppointmentStatus: `appointments`,
        getPackages: `appointments/bundles`,
        getCustomerBookingEligibility: `appointments/eligibility`,
        getPetBookingEligibility: (petId: string) => `appointments/eligibility/pets/${petId}`,
    },
    addresses: {
        getAddresses: 'addresses',
        addAddress: 'addresses',
        updateAddress: (addressId: number) => `addresses?addressId=${addressId}`,
        removeAddress: (addressId: number) => `addresses?addressId=${addressId}`,
    },
    product: {
        getProductBase: (id: string) => `v2/product/${id}/base?locale=${getLocale()}`,
        getProductTailored: `v2/product/tailored?locale=${getLocale()}`,
    },
    favorites: {
        get: () => `favorites?locale=${getLocale()}`,
        post: ({ sku }: { sku: string }) => `favorites/${sku}?locale=${getLocale()}`,
        delete: ({ sku }: { sku: string }) => `favorites/${sku}?locale=${getLocale()}`,
    },
    treatPoints: {
        get: () => `loyalty/points/estimate`,
    },
};

export const IDENTITY_ENDPOINTS = {
    consumeExchangeToken: 'v2/authentication/consumeExchangeToken',
    createExchangeToken: 'authentication/createExchangeToken',
};
